import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { FoxVillageLogo } from "brand";
import { useTranslation } from "i18n-fe";
import { parseFvdPushError } from "../util/fvdPushError";

// #554 - per-class Fox Village fox mark reflecting the class's aggregate
// FVD push state (score-api's `fvdPushState`, computed at read time by #567
// over the class's released rides): gray = NOT_POSTED, green = POSTED, red
// = FAILED. Uses `FoxVillageLogo` (the mark-only SVG, viewBox 0 0 219.26
// 332), not `Logo` (the full "fox mark + FOX VILLAGE wordmark" lockup used
// in this app's header, App.jsx's BrandLogoMark): at a row-height icon size
// the lockup's wordmark is illegible and the whole icon reads as colored
// fuzz, not as a status icon.
//
// Color comes from theme-aware MUI palette tokens, never `primary` - this
// app's brand theme (packages/brand/lib/ThemeBrand.js) sets primary.main to
// the brand darkGrey (#363636), which is near-invisible on a dark
// background (see ThemeBrand.js's own #813/#816 comments, and
// SheetStateChip/SheetSentChip which document the same trap). `text.disabled`,
// `success.main` and `error.main` are default MUI palette tokens the brand
// theme doesn't touch, so they render with correct contrast in both schemes.
const FVD_PUSH_DISPLAY = {
  NOT_POSTED: { color: "text.disabled", labelKey: "ring.fvdPush.notPosted" },
  POSTED: { color: "success.main", labelKey: "ring.fvdPush.posted" },
  FAILED: { color: "error.main", labelKey: "ring.fvdPush.failed" },
};

// `parseFvdPushError`'s `cause` is closed to "locked" | "unknown" | null
// (see the call site below) - static map, same shape as
// FvdPushFailureCauseChip's own CAUSE_DISPLAY, rather than interpolating
// `cause` into a dynamic i18n key (this repo requires static key literals).
const CAUSE_REASON_LABEL_KEY = {
  locked: "reviewQueue.fvdFailures.cause.locked",
  unknown: "reviewQueue.fvdFailures.cause.unknown",
  null: null,
};

export default function FvdPushStatusIcon({ fvdPushState, error, size = 18, ...props }) {
  const { t } = useTranslation("scoreadmin");
  // Unrecognized/missing state (e.g. older cached data) reads the same as
  // NOT_POSTED — score-api's own handler falls back the same way (class.js's
  // `pushStates.get(cls.id) ?? CLASS_PUSH_STATES.NOT_POSTED`).
  const state = FVD_PUSH_DISPLAY[fvdPushState] ? fvdPushState : "NOT_POSTED";
  const display = FVD_PUSH_DISPLAY[state];
  // Card #1886 AC3 — foxbox's OWN reported reason (Ride.fvdPushError, set
  // only alongside FAILED — see reportFvdPushStatus) surfaced here, but
  // never verbatim: the only shape this field ever actually carries is
  // foxbox's JSON blob (Foxbox.ShowData/PushStatusReport.cs —
  // {cause,attempts,rawError}, see util/fvdPushError.js's #1608 header), so
  // interpolating `error` raw into the tooltip put literal JSON in front of
  // a user (this card's own judge review caught it). `parseFvdPushError`
  // is the same parse ReviewQueue.jsx's fvd-failures table already applies
  // to this field — reuse it rather than re-deriving. `cause` maps through
  // the same `reviewQueue.fvdFailures.cause.*` keys that chip already uses,
  // then `rawError` (foxbox's own message, or the original string verbatim
  // when it wasn't JSON at all — never re-serialized, never braces) is
  // appended. `error` is optional so every other call site (ClassManager's
  // class-list icon, which has no per-ride error to show) is unaffected.
  const parsedError = error ? parseFvdPushError(error) : null;
  const causeLabelKey = parsedError ? CAUSE_REASON_LABEL_KEY[parsedError.cause] : null;
  const errorReason = parsedError
    ? [causeLabelKey ? t(causeLabelKey) : null, parsedError.rawError]
        .filter(Boolean)
        .join(": ")
    : null;
  const label = errorReason
    ? t("ring.fvdPush.reason", { label: t(display.labelKey), reason: errorReason })
    : t(display.labelKey);
  return (
    // Tooltip needs its child to forward a ref for anchoring; FoxVillageLogo
    // is a plain function component that doesn't, so the ref-forwarding node
    // is this `span` (matches SheetSentChip's `<Tooltip><span>...` pattern),
    // with the icon itself `aria-hidden` and the accessible name carried by
    // the span's own role="img"/aria-label.
    <Tooltip title={label}>
      <Box
        component="span"
        role="img"
        aria-label={label}
        data-testid="fvd-push-icon"
        data-fvd-push-state={state}
        sx={{ display: "inline-flex", color: display.color }}
        {...props}
      >
        <FoxVillageLogo aria-hidden="true" style={{ height: size, width: "auto", flexShrink: 0, display: "block" }} />
      </Box>
    </Tooltip>
  );
}
